<script setup>
import { computed } from 'vue'

const props = defineProps({
    row: {
        type: Object,
        required: true,
    },
    query: {
        type: String,
        default: '',
    },
    selected: {
        type: Boolean,
        default: false,
    },
    id: {
        type: String,
        required: true,
    },
})

const emit = defineEmits(['activate', 'remove'])

const isRecent = computed(() => props.row.kind === 'recent')
const isRound = computed(() => props.row.kind === 'artist')
const isTile = computed(() => props.row.kind === 'genre')

// Split the title around the first case-insensitive match so the match can be
// emphasised without v-html.
const pieces = computed(() => {
    const title = props.row.title || ''
    const needle = (props.query || '').trim().toLowerCase()
    const at = needle ? title.toLowerCase().indexOf(needle) : -1
    if (at < 0) return { before: title, match: '', after: '' }
    return { before: title.slice(0, at), match: title.slice(at, at + needle.length), after: title.slice(at + needle.length) }
})

function onRemove(event) {
    event.stopPropagation()
    emit('remove', props.row)
}
</script>

<template>
    <div
        :id="id"
        :class="['search-result-row', { 'search-result-row-selected': selected, 'search-result-row-recent': isRecent }]"
        role="option"
        :aria-selected="selected ? 'true' : 'false'"
        @click="emit('activate', row)"
    >
        <span :class="['art', { 'art-round': isRound || isRecent, 'art-tile': isTile || isRecent }]">
            <BaseIcon
                v-if="isRecent"
                name="history"
                :size="20"
            />
            <BaseIcon
                v-else-if="!row.image"
                name="browse"
                :size="20"
            />
            <BaseResourceImage
                v-else
                class="art-image"
                :resource="row.image"
                :type="row.imageType"
                :should-lazyload="false"
                :radius="isRound ? 'none' : 'list'"
            />
        </span>
        <span class="text">
            <span class="title">
                <span>{{ pieces.before }}</span><mark
                    v-if="pieces.match"
                    class="match"
                >{{ pieces.match }}</mark><span>{{ pieces.after }}</span>
            </span>
            <span
                v-if="!isRecent"
                class="type-line"
            >{{ row.typeLine }}</span>
        </span>
        <BaseButton
            v-if="isRecent"
            class="remove"
            variant="tertiary"
            size="s"
            icon-only
            icon="close"
            label="Remove"
            tabindex="-1"
            @click="onRemove"
        />
    </div>
</template>

<style lang="scss" scoped>
@use '@design' as *;

$art: 40px;

.search-result-row {
    display: flex;
    gap: $space-12;
    align-items: center;
    height: 56px;
    padding: 0 $space-8;
    cursor: pointer;
    border-radius: $radius-8;

    @include transition(background-color, fast);

    &.search-result-row-selected {
        background: $c-state-hover;
    }

    // Tablets render these rows — keep the highlight off after a tap.
    @media (hover: hover) {
        &:hover {
            background: $c-state-hover;
        }
    }
}

.art {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    color: $c-text-secondary;
    background: $c-surface-raised;
    border-radius: $radius-3;

    @include square($art);

    &.art-round {
        border-radius: $radius-full;
    }
}

.art-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.text {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-width: 0;
}

.title {
    @include bodySmall;

    overflow: hidden;
    text-overflow: ellipsis;
    color: $c-text-primary;
    white-space: nowrap;
}

.match {
    font-weight: 600;
    color: inherit;
    background: none;
}

.type-line {
    @include secondary;

    overflow: hidden;
    text-overflow: ellipsis;
    color: $c-text-tertiary;
    white-space: nowrap;
}

// [class] lifts the colour above BaseButton's 0,2,0 tertiary rule (the .x[class] recipe).
.remove[class] {
    flex-shrink: 0;
    color: $c-text-tertiary;
}

// Figma 554-1790: a recent row shows no × until it is hovered. Only hover-capable pointers
// hide it — on a tablet there is no hover, so the button has to stay reachable. It is hidden
// with visibility/opacity, never display, so revealing it cannot reflow the title.
@media (hover: hover) {
    .remove[class] {
        visibility: hidden;
        opacity: 0;

        // BaseButton's own root transition lives inside `:where()` (0,1,0), so this rule's
        // 0,3,0 shorthand replaces it wholesale — its hover colours and press release would
        // snap if they were left out. Re-declare them with the reveal, at `fast` to match the
        // row's own hover (BaseButton's root runs them at `base`).
        @include transition((opacity, background-color, color, border-color, transform), fast);
    }

    .search-result-row:hover .remove[class] {
        visibility: visible;
        opacity: 1;
    }
}

// Arrowing onto a recent must reveal what the row offers, at every pointer type.
.search-result-row-selected .remove[class] {
    visibility: visible;
    opacity: 1;
}
</style>
